// Layout configuration system for responsive design

// Layout types
export const LAYOUT_TYPES = {
  MOBILE_PORTRAIT: 'mobile_portrait',
  MOBILE_LANDSCAPE: 'mobile_landscape',
  TABLET_PORTRAIT: 'tablet_portrait',
  TABLET_LANDSCAPE: 'tablet_landscape',
  DESKTOP_PORTRAIT: 'desktop_portrait',
  DESKTOP_LANDSCAPE: 'desktop_landscape'
};

// Breakpoint definitions (in pixels)
export const BREAKPOINTS = {
  MOBILE_MAX: 768,
  TABLET_MAX: 1280
};

// Layout configurations
export const LAYOUT_CONFIGS = {
  // 1. MOBILE PORTRAIT (Classic Compact)
  [LAYOUT_TYPES.MOBILE_PORTRAIT]: {
    name: 'Mobile Portrait',
    description: 'Phone vertical',
    icon: 'smartphone',
    components: {
      header: {
        visible: true,
        variant: 'minimal', // Minimal header
        showLogo: true,
        showMotivationalMessage: false,
        showDate: false,
        showTodayStats: false,
        showLogout: false,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true,
        variant: 'compact',
        orientation: 'horizontal',
        showMonthLabels: false,
        showStats: false,
        dotSize: 'w-1.5 h-1.5',
        gap: 'gap-[0.1875rem]',
        padding: 'p-2',
        headerSize: 'text-sm',
        showLegend: false,
        showIcon: false,
        isCompact: true,
        maxHeight: '30%'
      },
      weekView: {
        visible: true,
        variant: 'compact',
        showWeekLabel: true,
        showStreaks: false,
        showProgress: false,
        buttonSize: 'h-10 w-10',
        starSize: 'w-4 h-4',
        plusSize: 'w-3 h-3',
        textSize: 'text-sm',
        dayTextSize: 'text-[0.625rem]',
        padding: 'p-2',
        layout: 'stacked'
      },
      weekProgress: {
        visible: false,
        progressSize: 'w-32 h-32',
        textSize: 'text-3xl',
        padding: 'p-4',
        cardColumns: 'grid-cols-1'
      }
    },
    spacing: {
      container: 'px-3 py-3 h-full flex flex-col',
      componentGap: 'space-y-2'
    }
  },

  // 2. MOBILE LANDSCAPE (Grid, Minimal Header)
  // In landscape the viewport is ~914×411 CSS — wide and SHORT. Every vertical
  // pixel spent on chrome (header, year card padding, legend) is 2× more visible
  // than in portrait. Be aggressive: smaller dots, no legend/icon, tighter
  // padding, smaller container gaps.
  [LAYOUT_TYPES.MOBILE_LANDSCAPE]: {
    name: 'Mobile Landscape',
    description: 'Phone horizontal',
    icon: 'smartphone',
    components: {
      header: {
        visible: true,
        variant: 'minimal', // Minimal header to save space
        showLogo: true,
        showMotivationalMessage: false,
        showDate: false,
        showTodayStats: false,
        showLogout: false,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true,
        variant: 'compact',
        orientation: 'horizontal',
        showMonthLabels: false,
        showStats: false,
        dotSize: 'w-1.5 h-1.5',     // was w-2 h-2 — smaller dots = more rows fit, year reads as a dense square instead of a 2-row strip
        gap: 'gap-[0.1875rem]',     // matches portrait-compact
        padding: 'p-2',             // was p-3
        headerSize: 'text-sm',      // was text-base
        showLegend: false,          // was true — legend alone was eating ~50px of the 411 CSS height
        showIcon: false,            // was true — Calendar icon + padded pill was another ~40px
        isCompact: true,
        maxHeight: '40%'            // was 30vh — at 411 CSS height that's ~124px, barely 2 rows; % of container is more predictable
      },
      weekView: {
        visible: true,
        variant: 'balanced',
        showWeekLabel: true,
        showStreaks: true,
        showProgress: true,
        buttonSize: 'h-8 w-8',
        starSize: 'w-4 h-4',
        plusSize: 'w-3 h-3',
        textSize: 'text-sm',
        dayTextSize: 'text-[0.625rem]',
        padding: 'p-2',
        layout: 'grid',
        nameColWidth: '8rem'
      },
      weekProgress: {
        visible: false,
        progressSize: 'w-32 h-32',
        textSize: 'text-3xl',
        padding: 'p-4',
        cardColumns: 'grid-cols-1'
      }
    },
    spacing: {
      container: 'px-3 py-1.5 h-full flex flex-col',  // was px-4 py-4
      componentGap: 'space-y-1.5'                      // was gap-2
    }
  },

  // 3. TABLET PORTRAIT (Stacked, Standard Header)
  [LAYOUT_TYPES.TABLET_PORTRAIT]: {
    name: 'Tablet Portrait',
    description: 'Tablet vertical',
    icon: 'tablet',
    components: {
      header: {
        visible: true,
        variant: 'standard', // Full header
        showLogo: true,
        showMotivationalMessage: true,
        showDate: true,
        showTodayStats: true,
        showLogout: true,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true,
        variant: 'standard',
        orientation: 'horizontal',
        showMonthLabels: true,
        showStats: true,
        dotSize: 'w-3 h-3',
        gap: 'gap-1.5',
        padding: 'p-6',
        headerSize: 'text-xl',
        showLegend: true,
        showIcon: true,
        isCompact: false
      },
      weekView: {
        visible: true,
        variant: 'standard',
        showWeekLabel: true,
        showStreaks: true,
        showProgress: true,
        buttonSize: 'h-20 w-20',
        starSize: 'w-5 h-5',
        plusSize: 'w-5 h-5',
        textSize: 'text-lg',
        dayTextSize: 'text-sm',
        padding: 'p-6',
        layout: 'stacked' // Stacked for portrait width
      },
      weekProgress: {
        visible: true, // Show progress below
        variant: 'standard',
        showGoalCompletion: true,
        showTimeAnalysis: true,
        showHabitStatus: true,
        progressSize: 'w-36 h-36 sm:w-44 sm:h-44',
        textSize: 'text-4xl sm:text-5xl',
        padding: 'p-6 sm:p-8',
        cardColumns: 'grid-cols-1 sm:grid-cols-2'
      }
    },
    spacing: {
      container: 'px-6 py-6 h-full flex flex-col',
      componentGap: 'space-y-6'
    }
  },

  // 4. TABLET LANDSCAPE (Grid, Standard Header)
  [LAYOUT_TYPES.TABLET_LANDSCAPE]: {
    name: 'Tablet Landscape',
    description: 'Tablet horizontal',
    icon: 'tablet',
    components: {
      header: {
        visible: true,
        variant: 'standard',
        showLogo: true,
        showMotivationalMessage: true,
        showDate: true,
        showTodayStats: true,
        showLogout: true,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true,
        variant: 'standard',
        orientation: 'horizontal',
        showMonthLabels: true,
        showStats: true,
        dotSize: 'w-3 h-3',
        gap: 'gap-1.5',
        padding: 'p-6',
        headerSize: 'text-xl',
        showLegend: true,
        showIcon: true,
        isCompact: false
      },
      weekView: {
        visible: true,
        variant: 'standard',
        showWeekLabel: true,
        showStreaks: true,
        showProgress: true,
        buttonSize: 'h-14 w-14',
        starSize: 'w-5 h-5',
        plusSize: 'w-5 h-5',
        textSize: 'text-lg',
        dayTextSize: 'text-sm',
        padding: 'p-6',
        layout: 'grid',
        nameColWidth: '11.25rem'
      },
      weekProgress: {
        visible: true,
        variant: 'standard',
        showGoalCompletion: true,
        showTimeAnalysis: true,
        showHabitStatus: true,
        progressSize: 'w-36 h-36 sm:w-44 sm:h-44',
        textSize: 'text-4xl sm:text-5xl',
        padding: 'p-6 sm:p-8',
        cardColumns: 'grid-cols-1 sm:grid-cols-2'
      }
    },
    spacing: {
      container: 'px-6 py-6 h-full flex flex-col',
      componentGap: 'space-y-6'
    }
  },

  // 5. DESKTOP PORTRAIT (Vertical Monitor)
  [LAYOUT_TYPES.DESKTOP_PORTRAIT]: {
    name: 'Desktop Portrait',
    description: 'Vertical monitor',
    icon: 'monitor',
    components: {
      header: {
        visible: true,
        variant: 'expanded',
        showLogo: true,
        showMotivationalMessage: true,
        showDate: true,
        showTodayStats: true,
        showLogout: true,
        showAdditionalMetrics: true,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true,
        variant: 'expanded',
        orientation: 'horizontal',
        showMonthLabels: true,
        showStats: true,
        dotSize: 'w-4 h-4',
        gap: 'gap-2',
        padding: 'p-8',
        headerSize: 'text-2xl',
        showLegend: true,
        showIcon: true,
        isCompact: false,
        showEnhancedTooltips: true
      },
      weekView: {
        visible: true,
        variant: 'expanded',
        showWeekLabel: true,
        showStreaks: true,
        showProgress: true,
        buttonSize: 'h-20 w-20',
        starSize: 'w-6 h-6',
        plusSize: 'w-6 h-6',
        textSize: 'text-xl',
        dayTextSize: 'text-base',
        padding: 'p-8',
        showEnhancedDetails: true,
        layout: 'stacked' // Stacked for vertical monitor
      },
      weekProgress: {
        visible: true,
        variant: 'expanded',
        showGoalCompletion: true,
        showTimeAnalysis: true,
        showHabitStatus: true,
        showDetailedBreakdown: true,
        progressSize: 'w-48 h-48',
        textSize: 'text-5xl',
        padding: 'p-8',
        cardColumns: 'grid-cols-1 sm:grid-cols-3'
      }
    },
    spacing: {
      container: 'px-8 py-8 h-full flex flex-col',
      componentGap: 'space-y-8'
    }
  },

  // 6. DESKTOP LANDSCAPE (Standard Wide)
  [LAYOUT_TYPES.DESKTOP_LANDSCAPE]: {
    name: 'Desktop Landscape',
    description: 'Standard monitor',
    icon: 'monitor',
    components: {
      header: {
        visible: true,
        variant: 'expanded',
        showLogo: true,
        showMotivationalMessage: true,
        showDate: true,
        showTodayStats: true,
        showLogout: true,
        showAdditionalMetrics: true,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true,
        variant: 'expanded',
        orientation: 'horizontal',
        showMonthLabels: true,
        showStats: true,
        dotSize: 'w-4 h-4',
        gap: 'gap-2',
        padding: 'p-8',
        headerSize: 'text-2xl',
        showLegend: true,
        showIcon: true,
        isCompact: false,
        showEnhancedTooltips: true
      },
      weekView: {
        visible: true,
        variant: 'expanded',
        showWeekLabel: true,
        showStreaks: true,
        showProgress: true,
        buttonSize: 'h-16 w-16',
        starSize: 'w-6 h-6',
        plusSize: 'w-6 h-6',
        textSize: 'text-xl',
        dayTextSize: 'text-base',
        padding: 'p-8',
        showEnhancedDetails: true,
        layout: 'grid',
        nameColWidth: '11.25rem'
      },
      weekProgress: {
        visible: true,
        variant: 'expanded',
        showGoalCompletion: true,
        showTimeAnalysis: true,
        showHabitStatus: true,
        showDetailedBreakdown: true,
        progressSize: 'w-48 h-48',
        textSize: 'text-5xl',
        padding: 'p-8',
        cardColumns: 'grid-cols-1 sm:grid-cols-3'
      }
    },
    spacing: {
      container: 'px-8 py-8 h-full flex flex-col',
      componentGap: 'space-y-8'
    }
  }
};

export const detectLayout = (width, height) => {
  const isLandscape = width > height;
  // Classify by the *shorter* side, not width. A phone in landscape is
  // 914×411 CSS — width alone would classify it as a tablet, but the 411
  // height means it needs mobile-dense chrome (small header, compact year
  // dots). Tablets have a min dimension ≥ ~800 regardless of orientation.
  const minDim = Math.min(width, height);

  if (minDim < BREAKPOINTS.MOBILE_MAX) {
    return isLandscape ? LAYOUT_TYPES.MOBILE_LANDSCAPE : LAYOUT_TYPES.MOBILE_PORTRAIT;
  } else if (minDim < BREAKPOINTS.TABLET_MAX) {
    return isLandscape ? LAYOUT_TYPES.TABLET_LANDSCAPE : LAYOUT_TYPES.TABLET_PORTRAIT;
  } else {
    return isLandscape ? LAYOUT_TYPES.DESKTOP_LANDSCAPE : LAYOUT_TYPES.DESKTOP_PORTRAIT;
  }
};

export const getLayoutConfig = (layoutType) => {
  return LAYOUT_CONFIGS[layoutType] || LAYOUT_CONFIGS[LAYOUT_TYPES.DESKTOP_LANDSCAPE];
};

export const getLayoutOptions = () => {
  return Object.keys(LAYOUT_CONFIGS).map(key => ({
    type: key,
    name: LAYOUT_CONFIGS[key].name,
    description: LAYOUT_CONFIGS[key].description,
    icon: LAYOUT_CONFIGS[key].icon
  }));
};

export const isLayoutSuitable = (layoutType, width) => {
  // Simplified check, could be more robust
  return true;
};

export const getModalConfig = (width) => {
  if (width < BREAKPOINTS.MOBILE_MAX) {
    return {
      type: 'fullscreen',
      animation: 'slideUp',
      position: 'bottom',
      width: '100%',
      height: '100%',
      closeButton: 'top-left'
    };
  } else {
    return {
      type: 'modal',
      animation: 'slideUp',
      position: 'center',
      width: 'max-w-3xl',
      height: 'max-h-[90vh]',
      closeButton: 'top-right'
    };
  }
};

const layoutConfigExports = {
  LAYOUT_TYPES,
  BREAKPOINTS,
  LAYOUT_CONFIGS,
  detectLayout,
  getLayoutConfig,
  getLayoutOptions,
  isLayoutSuitable,
  getModalConfig
};

export default layoutConfigExports;
